import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Drawer, Flex, Grid, Layout, Menu, Segmented, theme, Typography } from 'antd';
import { MenuOutlined, LogoutOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import globalListCategories from '../constants/globalListCategories.json';
import { useThemeContext } from '../app/ThemeProvider';
import isAdmin from '../helpers/isAdmin';
import logout from '../helpers/logout';
import { useDisabledModules } from '../hooks/useDisabledModules';

const { Text } = Typography;

function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const userInfo = useSelector((state) => state.login);
  const { mode: themeMode, setMode: setThemeMode } = useThemeContext();
  const screens = Grid.useBreakpoint();
  // Layout.Header is rendered outside antd's CSS-variable scope, so var(--ant-*)
  // resolves to nothing there and the sticky bar would be see-through.
  const { token } = theme.useToken();

  const [drawerOpen, setDrawerOpen] = useState(false);

  // The horizontal menu only fits from lg up; below that everything lives in the drawer.
  const isDesktop = Boolean(screens.lg);

  const userIsAdmin = isAdmin(userInfo?.role);
  const disabledModules = useDisabledModules();
  const hasSwapper = !disabledModules.includes('swapper');
  const hasOverpay = !disabledModules.includes('overpay');

  const menuItems = useMemo(() => {
    const listCategoryChildren = (globalListCategories || []).map((cat) => ({
      key: `/lists/${cat.value}`,
      label: cat.label || cat.value,
    }));

    const moreChildren = [
      { key: '/response-time', label: 'Response Time' },
      ...(hasOverpay ? [{ key: '/predictions', label: 'Predictions' }] : []),
      ...(userIsAdmin && hasSwapper ? [{ key: '/swapper-bulk-manager', label: 'Swapper Bulk Manager' }] : []),
      { key: '/documentation', label: 'Documentation' },
      { key: '/event-logs', label: 'Event Logs' },
      { key: '/settings', label: 'Settings' },
      ...(userIsAdmin ? [{ key: '/user-management', label: 'User Management' }] : []),
    ];

    return [
      { key: '/finances-v2', label: 'Finances' },
      { key: '/companies', label: 'Companies' },
      { key: '/ssps', label: 'SSPs' },
      { key: '/dsps', label: 'DSPs' },
      { key: '/usersync-statistics', label: 'Usersync Stats' },
      { key: '/data-sample', label: 'Data Sample' },
      { key: 'lists-parent', label: 'Lists', children: listCategoryChildren },
      { key: 'more-parent', label: '•••', children: moreChildren },
    ];
  }, [userIsAdmin, hasSwapper, hasOverpay]);

  const selectedKeys = useMemo(() => {
    const path = location.pathname;
    if (path.startsWith('/lists/')) return [path];
    if (path.startsWith('/dsps')) return ['/dsps'];
    if (path.startsWith('/ssps')) return ['/ssps'];
    return [path];
  }, [location.pathname]);

  const handleMenuClick = ({ key }) => {
    if (!key || !key.startsWith('/')) return;
    navigate(key);
    if (key.startsWith('/lists/')) {
      // legacy behavior: force reload so list page refetches category
      navigate(0);
    }
    setDrawerOpen(false);
  };

  const themeSwitcher = (
    <Segmented
      size="small"
      value={themeMode}
      onChange={(value) => setThemeMode(value)}
      options={[
        { label: 'Light', value: 'light' },
        { label: 'Dark', value: 'dark' },
        { label: 'System', value: 'system' },
      ]}
      aria-label="Theme"
    />
  );

  return (
    <Layout.Header
      className="xchange-header"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        background: token.colorBgContainer,
        borderBottom: `1px solid ${token.colorBorderSecondary}`,
      }}
    >
      <Flex align="center" justify="space-between" gap="small" style={{ width: '100%' }}>
        <Flex align="center" gap="small" style={{ minWidth: 0 }}>
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={() => setDrawerOpen(true)}
            className="xchange-mobile-menu-trigger"
            aria-label="Open navigation"
          />
          <Text strong style={{ cursor: 'pointer', fontSize: 16, whiteSpace: 'nowrap' }} onClick={() => navigate('/')}>
            XChange
          </Text>
        </Flex>

        <div className="xchange-desktop-menu" style={{ flex: 1, minWidth: 0 }}>
          <Menu
            mode="horizontal"
            items={menuItems}
            onClick={handleMenuClick}
            selectedKeys={selectedKeys}
            style={{ borderBottom: 'none', lineHeight: '62px' }}
          />
        </div>

        {/* Below lg the theme switch, account name and logout move into the drawer:
            they cannot fit next to the brand on a phone without wrapping. */}
        {isDesktop ? (
          <Flex align="center" gap="small">
            {themeSwitcher}
            {userInfo?.login && (
              <Text type="secondary" title={userInfo.login} style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                {userInfo.login}
              </Text>
            )}
            <Button type="link" danger icon={<LogoutOutlined />} onClick={logout}>
              Log Out
            </Button>
          </Flex>
        ) : (
          <Button type="text" danger icon={<LogoutOutlined />} onClick={logout} aria-label="Log out" title="Log out" />
        )}
      </Flex>

      <Drawer
        title="Navigation"
        placement="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        size={280}
        styles={{ body: { padding: 0 } }}
        footer={
          <Flex vertical gap="small" align="flex-start">
            {userInfo?.login && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {userInfo.login}
              </Text>
            )}
            {themeSwitcher}
          </Flex>
        }
      >
        <Menu mode="inline" items={menuItems} onClick={handleMenuClick} selectedKeys={selectedKeys} />
      </Drawer>
    </Layout.Header>
  );
}

export default Header;
